/**
 * Utility functions for SEO optimization
 */
import {
  CITY_SEO,
  getCitySeoCount as _getCitySeoCount,
  getCityPriceRangeString,
} from '../../../shared/citySeoData.cjs';

/**
 * Static SEO data per city — derived from the single source of truth in
 * shared/citySeoData.cjs.  The `priceRange` string and `label` are kept
 * for backward-compat with existing consumers.
 */
export const CITY_SEO_DATA = Object.fromEntries(
  Object.entries(CITY_SEO).map(([city, d]) => [
    city,
    {
      seoCount: d.seoCount,
      minPrice: d.minPrice,
      priceRange: getCityPriceRangeString(city),
      label: d.label,
    },
  ])
);

/**
 * Returns the stable SEO count for a city (for use in titles/descriptions).
 * Falls back to 10 for unknown cities.
 * @param {string} city
 * @returns {number}
 */
export function getCitySeoCount(city) {
  return _getCitySeoCount(city, 10);
}

/**
 * Validates and sanitizes meta descriptions to optimal length
 * @param {string} description - The description text to validate
 * @param {number} maxLength - Maximum allowed length (default: 160)
 * @returns {string} - The sanitized description
 */
export function sanitizeMetaDescription(description, maxLength = 160) {
  if (!description) return '';
  
  // Remove any HTML tags
  let sanitized = description.replace(/<[^>]*>/g, '');
  
  // Trim and limit length
  sanitized = sanitized.trim();
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength - 3) + '...';
  }
  
  return sanitized;
}

/**
 * Generates image metadata for social sharing
 * @param {string} imageUrl - The URL of the image
 * @param {string} fallbackUrl - Fallback image URL if primary is not provided
 * @param {Object} dimensions - Image dimensions { width, height }
 * @returns {Object} - Image metadata for OG tags
 */
export function getImageMetadata(imageUrl, fallbackUrl, dimensions = { width: 1200, height: 630 }) {
  const url = imageUrl || fallbackUrl;
  
  return {
    url,
    width: dimensions.width,
    height: dimensions.height,
    alt: 'Coolliving - Encuentra tu coliving ideal'
  };
}

/**
 * Generates canonical URL for the current page
 * @param {string} path - The current path
 * @param {string} siteUrl - The base site URL
 * @returns {string} - The canonical URL
 */
export function getCanonicalUrl(path, siteUrl) {
  // Ensure path starts with a slash and remove trailing slash
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const trimmedPath = normalizedPath.endsWith('/') && normalizedPath.length > 1 
    ? normalizedPath.slice(0, -1) 
    : normalizedPath;
  
  // Ensure siteUrl doesn't end with a slash
  const baseUrl = siteUrl.endsWith('/') 
    ? siteUrl.slice(0, -1) 
    : siteUrl;
  
  return `${baseUrl}${trimmedPath}`;
}

/**
 * Creates SEO-friendly page title
 * @param {string} title - The page title
 * @param {string} siteName - The site name
 * @returns {string} - Formatted page title
 */
export function formatPageTitle(title, siteName = 'Coolliving') {
  if (!title) return siteName;
  return `${title} | ${siteName}`;
}

export {
  getGscSearchMeta,
  getGscLandingKeywords,
  GSC_ES_CITY_NAMES,
} from '../../../shared/gscSearchIntent.cjs'; 